/*
 * Wholesale application — US address type-ahead (Geoapify).
 *
 * PROGRESSIVE ENHANCEMENT ONLY. This file must never be required for the form to
 * work. No key, no network, blocked request, JS error — in every case the form
 * falls back to the structured fields plus native browser autofill, exactly as it
 * behaves with this script absent. Nothing in the submit path depends on it.
 *
 * Why it exists: sales tax is destination-sourced, so State and ZIP are the
 * highest-value fields on this form. Choosing a verified suggestion removes typos
 * in precisely the fields that decide NY vs out-of-state treatment.
 *
 * Reads the key from data-geoapify-key on the form (set from the theme setting),
 * so the key is never hardcoded here.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://api.geoapify.com/v1/geocode/autocomplete';
  var MIN_CHARS = 3;
  var DEBOUNCE_MS = 250;
  var LIMIT = 6;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* Geoapify returns results[] for format=json but features[].properties for the
     GeoJSON default. The published docs are inconsistent, so accept either. */
  function normalise(data) {
    if (!data) return [];
    if (Array.isArray(data.results)) return data.results;
    if (Array.isArray(data.features)) {
      return data.features.map(function (f) { return (f && f.properties) || {}; });
    }
    return [];
  }

  function streetOf(p) {
    if (p.address_line1) return p.address_line1;
    var parts = [];
    if (p.housenumber) parts.push(p.housenumber);
    if (p.street) parts.push(p.street);
    return parts.join(' ');
  }

  /*
   * Resolving the mailing city is not simply p.city.
   *
   * Geoapify is OSM-derived, so across much of New York State (and other
   * "town of" states) p.city holds the ADMINISTRATIVE unit, not the postal city.
   * Real example: 1600 Pennsylvania Avenue, NY 12084 returns
   *   city: "Town of Guilderland"   <- administrative
   *   village: "Westmere"           <- the actual mailing city
   *
   * Conversely p.suburb must NEVER outrank p.city — it is a neighbourhood.
   * Real examples: Springfield MA / suburb "East Forest Park", and
   * East Lyme CT / suburb "Niantic". The city is correct in both.
   *
   * Order below was validated against live Geoapify responses for NY, CT and MA.
   */
  var ADMIN_PREFIX = /^(town|city|village|township|borough|municipality|charter township)\s+of\s+/i;

  function cityOf(p) {
    if (p.city && !ADMIN_PREFIX.test(p.city)) return p.city;
    var specific = p.village || p.town || p.municipality;
    if (specific) return specific;
    if (p.city) return p.city.replace(ADMIN_PREFIX, '');
    if (p.suburb) return p.suburb;
    if (p.county) return p.county.replace(/\s+(County|Parish|Borough)$/i, '');
    return '';
  }

  /* The dropdown label repeats the country on every row; drop it for readability. */
  function labelOf(p) {
    var t = p.formatted || streetOf(p);
    return t.replace(/,\s*United States(\s+of\s+America)?\s*$/i, '');
  }

  /* Only assign a <select> value if the option actually exists, otherwise the
     field silently becomes empty and we lose the tax-critical state. */
  function setSelect(el, value) {
    if (!el || !value) return;
    var match = Array.prototype.slice.call(el.options).filter(function (o) {
      return o.value.toLowerCase() === String(value).toLowerCase();
    })[0];
    if (match) el.value = match.value;
  }

  function setValue(el, value) {
    if (el && value) el.value = value;
  }

  function AddressAutocomplete(block, apiKey) {
    var street = block.querySelector('[data-address-street]');
    var list = block.querySelector('[data-address-suggestions]');
    if (!street || !list) return;

    var prefix = block.getAttribute('data-address-block') || '';
    var timer = null;
    var controller = null;
    var items = [];
    var active = -1;

    function sibling(suffix) {
      return block.querySelector('[data-address-field="' + suffix + '"]');
    }

    function close() {
      list.innerHTML = '';
      list.hidden = true;
      street.setAttribute('aria-expanded', 'false');
      street.removeAttribute('aria-activedescendant');
      active = -1;
      items = [];
    }

    function choose(p) {
      setValue(street, streetOf(p));
      setValue(sibling('city'), cityOf(p));
      setSelect(sibling('state'), p.state);
      setValue(sibling('zip'), p.postcode);
      setValue(sibling('country'), p.country);
      [street, sibling('city'), sibling('state'), sibling('zip'), sibling('country')]
        .forEach(function (el) {
          if (el) el.dispatchEvent(new Event('change', { bubbles: true }));
        });
      close();
      street.focus();
    }

    function highlight(i) {
      var opts = list.querySelectorAll('[role="option"]');
      Array.prototype.forEach.call(opts, function (o, idx) {
        var on = idx === i;
        o.classList.toggle('is-active', on);
        o.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      if (i >= 0 && opts[i]) {
        street.setAttribute('aria-activedescendant', opts[i].id);
        if (opts[i].scrollIntoView) opts[i].scrollIntoView({ block: 'nearest' });
      } else {
        street.removeAttribute('aria-activedescendant');
      }
      active = i;
    }

    function render(results) {
      items = results;
      if (!results.length) { close(); return; }
      list.innerHTML = '';
      results.forEach(function (p, i) {
        var li = document.createElement('li');
        li.id = 'addr-opt-' + prefix + '-' + i;
        li.className = 'wholesale-address__suggestion';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.textContent = labelOf(p);
        li.addEventListener('mousedown', function (e) { e.preventDefault(); choose(p); });
        list.appendChild(li);
      });
      list.hidden = false;
      street.setAttribute('aria-expanded', 'true');
      highlight(-1);
    }

    function search(text) {
      if (controller) controller.abort();
      controller = ('AbortController' in window) ? new AbortController() : null;

      var url = ENDPOINT +
        '?text=' + encodeURIComponent(text) +
        '&filter=countrycode:us' +
        '&format=json' +
        '&limit=' + LIMIT +
        '&apiKey=' + encodeURIComponent(apiKey);

      fetch(url, controller ? { signal: controller.signal } : undefined)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) { render(normalise(data)); })
        .catch(function () { /* aborted, offline, bad key — stay silent, form still works */ });
    }

    street.setAttribute('role', 'combobox');
    street.setAttribute('aria-autocomplete', 'list');
    street.setAttribute('aria-expanded', 'false');
    street.setAttribute('aria-controls', list.id);

    street.addEventListener('input', function () {
      var v = street.value.trim();
      if (timer) clearTimeout(timer);
      if (v.length < MIN_CHARS) { close(); return; }
      timer = setTimeout(function () { search(v); }, DEBOUNCE_MS);
    });

    street.addEventListener('keydown', function (e) {
      if (list.hidden || !items.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); highlight((active + 1) % items.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(active <= 0 ? items.length - 1 : active - 1); }
      else if (e.key === 'Enter' && active > -1) { e.preventDefault(); choose(items[active]); }
      else if (e.key === 'Escape') { close(); }
      else if (e.key === 'Tab') { close(); }
    });

    street.addEventListener('blur', function () { setTimeout(close, 150); });
  }

  ready(function () {
    document.querySelectorAll('form.contact-form[data-geoapify-key]').forEach(function (form) {
      var key = form.getAttribute('data-geoapify-key');
      if (!key) return;
      form.querySelectorAll('[data-address-block]').forEach(function (block) {
        try { AddressAutocomplete(block, key); } catch (err) { /* never break the form */ }
      });
    });
  });
})();
